import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  buildFirmwareHeaderLabel,
  canEditLatestFirmware,
  validateLatestFirmware,
} from "../utils/liftFirmware";

/**
 * TE-2011: Firmware Version column header, showing the latest released version
 * and letting a Rehrig admin edit it in place.
 *
 * DSS need to update this each time Pulsar ships a release without a TE ticket
 * or an NPD deploy, so the value is stored in SSM and edited here. The editing
 * affordance follows the Customer Alias pattern in LiftDataTable (pencil, then
 * confirm/cancel icons) so it looks native to the grid.
 *
 * The role check here only hides the control. Authorization is enforced by the
 * PUT endpoint, which is what actually matters: the shared custom authorizer
 * does not currently gate paths by role.
 *
 * @param {?string} latestVersion   Current value, null when unset.
 * @param {?string} error           Message from a failed save, if any.
 * @param {string}  userRoleName    Signed-in role.
 * @param {Function} onSave         Called with the new value string.
 */
function FirmwareVersionHeader({ latestVersion, error, userRoleName, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState(null);

  const canEdit = canEditLatestFirmware(userRoleName);
  const label = buildFirmwareHeaderLabel(latestVersion);

  // Surface a rejected save by reopening the editor with the value still in it,
  // so the user can correct rather than retype.
  useEffect(() => {
    if (error) {
      setIsEditing(true);
    }
  }, [error]);

  const startEditing = (event) => {
    // The header is also the grid's sort control; editing must not sort.
    event.stopPropagation();
    setDraft(latestVersion || "");
    setLocalError(null);
    setIsEditing(true);
  };

  const cancel = (event) => {
    if (event) event.stopPropagation();
    setIsEditing(false);
    setDraft("");
    setLocalError(null);
  };

  const save = (event) => {
    if (event) event.stopPropagation();
    const message = validateLatestFirmware(draft);
    if (message) {
      setLocalError(message);
      return;
    }
    onSave(draft.trim());
    setIsEditing(false);
  };

  if (isEditing && canEdit) {
    const shownError = localError || error;
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "2px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* variant="standard" and the icon colours match the Customer Alias
            editor in LiftDataTable, so the two read as the same control. */}
        <TextField
          size="small"
          variant="standard"
          autoFocus
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setLocalError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save(e);
            if (e.key === "Escape") cancel(e);
          }}
          error={Boolean(shownError)}
          helperText={shownError || ""}
          placeholder="3.10.1"
          inputProps={{
            "aria-label": "Latest firmware version",
            maxLength: 64,
            style: { fontSize: "13px" },
          }}
          sx={{ width: 120 }}
        />
        {/* Wrapped in IconButton rather than a bare icon with onClick, which is
            what the alias editor does: a bare icon is not keyboard reachable. */}
        <Tooltip title="Save">
          <IconButton
            size="small"
            onClick={save}
            aria-label="Save latest firmware version"
            data-testid="firmware-header-save"
          >
            <CheckIcon sx={{ fontSize: "16px", color: "#018015" }} />
          </IconButton>
        </Tooltip>
        {/* The alias editor has no cancel. This header is also the grid's sort
            control, so a user who opens the editor by accident needs a way out
            that is not committing a value. */}
        <Tooltip title="Cancel">
          <IconButton
            size="small"
            onClick={cancel}
            aria-label="Cancel editing latest firmware version"
            data-testid="firmware-header-cancel"
          >
            <CloseIcon sx={{ fontSize: "16px", color: "#58595B" }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
      <span className={"grid-column-header"} data-testid="firmware-header-label">
        {label}
      </span>
      {canEdit && (
        <Tooltip title="Edit latest version">
          <IconButton
            size="small"
            onClick={startEditing}
            aria-label="Edit latest firmware version"
            data-testid="firmware-header-edit"
          >
            <EditIcon sx={{ fontSize: "14px", color: "#2027A5" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default FirmwareVersionHeader;
